<template>
  <sf-dialog v-model="dialog" @keydown.esc="close">
    <template #activator="{ props }">
      <v-list-item v-bind="props" :prepend-icon="mdiAccountSwitchOutline">
        <v-list-item-title>
          {{ $t('switchSubsidiary') }}
        </v-list-item-title>
      </v-list-item>
    </template>
    <v-card :loading="loading">
      <v-card-item>
        <v-card-title>
          <sf-heading>{{ $t('switchSubsidiary') }}</sf-heading>
        </v-card-title>
      </v-card-item>
      <v-card-text>
        <sf-text>{{ $t('switchSubsidiaryHint') }}</sf-text>
        <v-autocomplete
          v-model="subsidiary"
          v-model:search="searchTerm"
          return-object
          color="primary"
          class="mt-2 combobox-role"
          variant="outlined"
          density="compact"
          clearable
          hide-details
          :placeholder="$t('search')"
          :maxlength="SEARCH_FIELD_MAX_LENGTH"
          :filter="false"
          :items="subsidiaries"
          item-title="name"
          item-value="id"
          @update:search="onSearch"
        >
          <template #item="{ item, index, props }">
            <v-list-item
              v-bind="props"
              :key="item.name"
              v-intersect.once="
                index === subsidiaries.length - Math.round(PAGE_SIZE / 2) ? onIntersect : ''
              "
            >
              <template #title>
                {{ item?.name }}
              </template>
            </v-list-item>
          </template>
          <template #no-data>
            <v-list-item v-if="!loading && subsidiaries.length == 0">
              <template #title>
                {{ $t('noDataAvailable') }}
              </template>
            </v-list-item>
          </template>
        </v-autocomplete>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <sf-text-button ref="send-button" :disabled="!subsidiary" @click="switchToSubsidiary"
          >{{ $t('switchSubsidiary') }}
        </sf-text-button>
      </v-card-actions>
    </v-card>
  </sf-dialog>
</template>

<script setup lang="ts">
import SfTextButton from '@/components/button/SfTextButton.vue'
import SfDialog from '@/components/dialog/SfDialog.vue'
import SfHeading from '@/components/text/SfHeading.vue'
import SfText from '@/components/text/SfText.vue'
import { SEARCH_FIELD_MAX_LENGTH } from '@/modules/search/constants'
import useSubsidiary from '@/modules/subsidiary/useSubsidiary'
import { useSubsidiarySearch } from '@/modules/subsidiary/useSubsidiarySearch'
import MyCompanyEvent from '@/modules/tracking/events/MyCompany'
import { trackEvent } from '@/modules/tracking/useTracking'
import { PAGE_SIZE } from '@/types'
import { Subsidiary } from '@/types/index'
import { mdiAccountSwitchOutline } from '@mdi/js'
import { ref, watch } from 'vue'

const emits = defineEmits(['close'])
const dialog = ref(false)
const searchTerm = ref<string | undefined>('')
const { subsidiaries, loading, searchSubsidiaries, loadNextPage, clearSubsidiaries } =
  useSubsidiarySearch()
const subsidiary = ref<Subsidiary | undefined>(undefined)

const switchToSubsidiary = () => {
  if (subsidiary.value) {
    trackEvent(MyCompanyEvent.ChangeSubsidiary)
    useSubsidiary().switchToSubsidiary(subsidiary.value.id)
  }
}

const close = () => {
  dialog.value = false
  emits('close')
}

watch(dialog, (isOpen) => {
  if (isOpen) {
    searchSubsidiaries()
  }
})

const onIntersect = () => {
  loadNextPage()
}

const timerId = ref<NodeJS.Timeout>()

const onSearch = () => {
  clearTimeout(timerId.value)

  // prevent unnecessary requests
  const keyword = searchTerm.value
  const subsidiaryName = subsidiary?.value?.name
  if ((subsidiary.value && keyword === subsidiaryName) || keyword == undefined) {
    return
  }

  timerId.value = setTimeout(() => {
    const trimmed = keyword.trim()
    clearSubsidiaries()
    searchSubsidiaries(trimmed)
  }, 400)
}
</script>
